import Typography from "@portal/components/atoms/v2/typography";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import useSavingProposal from "../../../../hooks/robot/useSavingProposal";
import { useEffect, useMemo } from "react";
import { MainBox } from "../components/mainBox";
import { SavingSuggestionData } from "../components/highcharts/SavingSuggestionData";
import { MapFundsToList, MapFundsToSavingsAgreement } from "./helpers";
import { SavingSuggestionFunds } from "./components/SavingSuggestionFunds";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { useAppDispatch, useAppSelector } from "@portal/redux/store";
import { RobotFooterComponent } from "../components/footer";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import { setSavingsAgreementFunds } from "@portal/redux/reducers/ui/createSavingsAgreement";
import Link from "next/link";
import Icon from "@portal/components/atoms/icons";
import PageSpinner from "@portal/components/molecules/v2/pageSpinner";

export const SavingSuggestionPage = ({ centerId, pageIndex, isDefaultLanguage }: IPageProps) => {
  const dispatch = useAppDispatch();
  const savingProposalRequest = useAppSelector(state => state.persisted.savingProposal);
  const savingProposalResponse = useSavingProposal({ ...savingProposalRequest, centerId });
  const isLoading = savingProposalResponse.isLoading;
  const data = savingProposalResponse.data?.funds;
  const funds = useMemo(() => MapFundsToList(savingProposalResponse), [savingProposalResponse]);
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;

  useEffect(() => {
    if (data) {
      const agreementFunds = MapFundsToSavingsAgreement(data);
      if (agreementFunds) dispatch(setSavingsAgreementFunds(agreementFunds));
    }
  }, [data, dispatch]);

  return (
    <>
      <MainBox>
        <div className="flex flex-col mb-2 border rounded-md w-full p-1 bg-white h-[400px] ">
          {isLoading ? <PageSpinner /> : <HighchartsReact highcharts={Highcharts} options={SavingSuggestionData(data)} />}
        </div>
        {data && data.length > 0 && <Typography variant="Bold">{texts.PAGE_14_KIID_HEADER}</Typography>}
        {data?.map(fund => (
          <div key={fund.isin} className="flex">
            <Link
              className="flex items-center space-x-1"
              target="_blank"
              href={`https://doc.morningstar.com/LatestDoc.aspx?clientid=norne&key=388b5494c68c4cf1&isin=${fund.isin}&documenttype=74&language=467`}
              prefetch={false}
            >
              <Icon name={"paperClipIcon"} size="xsmall" />
              <Typography variant="BodySpan">{fund.name}</Typography>
            </Link>
          </div>
        ))}

        <div className="flex flex-col my-2 w-full">
          <Typography variant="Body">{texts.PAGE_14_INFO_TEXT}</Typography>
        </div>
        <div className="flex flex-col my-2">
          <Typography variant="H6">{texts.PAGE_14_PORTFOLIO}</Typography>
          <TextLine />
        </div>
        <SavingSuggestionFunds isDefaultLanguage={isDefaultLanguage} funds={funds} isLoading={isLoading} />
      </MainBox>
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={isLoading}
      />
    </>
  );
};
